import omit from 'lodash/omit';
import { useCallback, useRef } from 'react';
import { devWarning } from '@tanium/coreui-utils';
import {
  AdvancedFiltersContextType,
  FilterValue,
  UpdateFilterArgs,
} from '@tanium/react-advanced-filters-context';
import { useControlledProp } from '@tanium/react-use-controlled-prop';

export const FILTER_BUILDER_KEY = 'filterBuilder';

export interface UseAdvancedFiltersReturnValue
  extends Pick<AdvancedFiltersContextType, 'clear' | 'drafts'> {
  updateFilter: AdvancedFiltersContextType['updateFilterValue'];
  pills: string[];
  value: { [key: string]: FilterValue };
}

export interface AdvancedFilterChangeEvent {
  value: UseAdvancedFiltersProps['value'];
  filterValues: { [key: string]: any }; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export type OnChangeEventHandler = (event: AdvancedFilterChangeEvent) => void;

export interface UseAdvancedFiltersProps {
  /**
   * The value for the AdvancedFilter component. Contains all filter values including pill values displayed
   * in the header.
   * Keys correspond to the `dataKey`s
   * assigned to AdvancedFilterInput components. Values are supplied directly by the AdvancedFilterInput.
   */
  value?: { [key: string]: FilterValue };
  /**
   * Value of the filters upon initialization. This has no effect if `value` is defined.
   */
  defaultValue?: UseAdvancedFiltersProps['value'];
  /**
   * Callback triggered when the AdvancedFilter's value changes. This callback makes two objects
   * accessible:
   * - the `filterValues` object, which holds the values of all the AdvancedFilterInputs in the
   *   AdvancedFilters;
   * - the `values` object, which in addition to the values of the AdvancedFilterInputs, holds the
   *   values of the pills in the header.
   *
   * If the AdvancedFilter is not controlled, the `filterValues` parameter of the callback will be
   * more succinct.
   */
  onChange?: OnChangeEventHandler;
}

export const validateValues = (values: UseAdvancedFiltersProps['value']) => {
  const keys = Object.keys(values || {});
  const filterBuilderExists = !!keys.find((k) => k === FILTER_BUILDER_KEY);
  devWarning(
    () => !filterBuilderExists || (filterBuilderExists && keys.length === 1),
    `Filter Builder should not be applied when other filters are set.`,
  );
};

const getFilterValues = (updatedValue: NonNullable<UseAdvancedFiltersProps['value']>) =>
  Object.keys(updatedValue).reduce((acc, key) => ({ ...acc, [key]: updatedValue[key].value }), {});

export const useAdvancedFiltersValues = ({
  value: valueProp,
  defaultValue = {},
  onChange,
}: UseAdvancedFiltersProps): UseAdvancedFiltersReturnValue => {
  const drafts = useRef<{ [key: string]: unknown }>({});
  const [value, setValue] = useControlledProp({
    defaultValue,
    componentName: 'AdvancedFilters',
    onUpdate: ({ value: updatedValue }) => {
      if (onChange) {
        const values = getFilterValues(updatedValue);
        onChange({ value: updatedValue, filterValues: values });
      }
    },
    propName: 'value',
    value: valueProp,
  });
  const getPills = useCallback(
    () => Object.keys(value).filter((key) => !!value[key].pill),
    [value],
  );

  const clear = useCallback(() => {
    setValue({});
  }, [setValue]);

  const updateFilter = useCallback(
    ({
      dataKey,
      value: updateValue,
      label,
      displayValue,
      replaceExisting = false,
    }: UpdateFilterArgs) => {
      const shouldRemovePill = updateValue === undefined;
      if (shouldRemovePill) {
        setValue((oldValue) => omit(oldValue, dataKey));
      } else {
        const pill = { label, displayValue };
        setValue((oldValue) => {
          const newKeyValue = {
            [dataKey]: { pill, value: updateValue },
          };

          const newValue = {
            ...(replaceExisting ? {} : oldValue),
            ...newKeyValue,
          };

          return newValue as NonNullable<UseAdvancedFiltersProps['value']>;
        });
      }
    },
    [setValue],
  );

  if (process.env.NODE_ENV !== 'production') {
    validateValues(value);
  }

  return {
    clear,
    value,
    updateFilter,
    pills: getPills(),
    drafts: drafts.current,
  };
};
